import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRemembered } from '../useRemembered'
import { createPortal } from 'react-dom'
import type { SkillDefinition } from '@shared/types'
import { arrangeBy, readSkillOrder, saveSkillOrder, withRetry } from '@shared/services'
import { spectrumColor } from '@shared/data/brandColors'
import { useDragOrder } from '@shared/hooks/useDragOrder'
import { useTileInteraction } from '../hooks/useTileInteraction'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/** A card's height on the rail and the space between two (canvas.css). */
const RAIL_CARD = 40
const RAIL_GAP = 6

interface SkillRailProps {
  skills: SkillDefinition[]
  /** The project the rail is on. Its open, narrow or shut state is the person's, not the project's, since 2026-10-22. */
  projectId: string
  /** Called when a card is let go over the canvas, in screen coordinates. */
  onDrop: (skill: SkillDefinition, glow: string, clientX: number, clientY: number) => void
}

interface Dragging {
  skill: SkillDefinition
  glow: string
  x: number
  y: number
}

/**
 * The rail of skills, and dragging one onto the canvas.
 *
 * COLLAPSED BY A CLICK, NOT BY THE POINTER LEAVING
 * An earlier design had it float out on hover. It was dropped, and the reason
 * is worth keeping: a panel that opens on hover and closes when the pointer
 * leaves cannot be dragged out of. You press a card, pull towards the canvas,
 * the pointer crosses the panel's edge, the panel decides you have left and
 * closes — taking the thing in your hand with it. A click has no such opinion.
 *
 * ONE DRAG, TWO OUTCOMES (Huanrui, 2026-10-02)
 * Dragged up and down inside the rail, a card changes places with the others,
 * and that order is remembered for this person (the canvas's own, apart from
 * the overview's). Dragged out of the rail, it becomes a ghost that follows
 * the pointer and lands on the canvas where it is let go -- as before. Pins
 * are gone from here.
 *
 * DRAGGING IS POINTER EVENTS, NOT THE BROWSER'S DRAG AND DROP
 * The native API cannot be told what the thing under the cursor should look
 * like, and behaves differently on a trackpad, a touch screen and a mouse.
 * Here the card that follows the cursor is an ordinary element, so it can glow
 * the way the overview's cards do.
 */
export function SkillRail({ skills, projectId, onDrop }: SkillRailProps) {
  /**
   * Remembered, like the rest of how this page is arranged. Somebody who keeps
   * the rail shut was re-shutting it on every visit.
   */
  // Kept with the person, not the project (Huanrui, 2026-10-22): the rail is
  // theirs, the same on every board and every computer (uiPrefs.ts).
  const [open, setOpen] = useRemembered('ww.canvas.rail', true)
  /**
   * Three states, one ball (Huanrui, 2026-10-22): shut, the ball alone; one
   * press and the icons grow up out of it as a column with the ball at its
   * foot; another and the column widens to the left into the full rail with
   * names; another and it folds down into the ball again. The narrow one is
   * remembered like `open`; until it is changed, the rail opens on the icons.
   */
  const [narrow, setNarrow] = useRemembered('ww.canvas.railNarrow', true)
  const [drag, setDrag] = useState<Dragging | null>(null)
  /**
   * The name beside an icon, icons only (Huanrui, 2026-10-22, after Miro's):
   * a small dark bubble to the icon's left with a point towards it. Fixed to
   * the window, because the list clips anything that leaves it.
   */
  const [tip, setTip] = useState<{ name: string; right: number; top: number } | null>(null)
  const showTip = useCallback((name: string | null, card?: HTMLElement) => {
    if (!name || !card) {
      setTip(null)
      return
    }
    const box = card.getBoundingClientRect()
    setTip({ name, right: window.innerWidth - box.left + 10, top: box.top + box.height / 2 })
  }, [])
  /** Growing out of the ball, and folding back into it: each runs its animation first. */
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const toggle = () => {
    if (closing || opening) return
    if (!open) {
      setNarrow(true)
      setOpen(true)
      setOpening(true)
      window.setTimeout(() => setOpening(false), 300)
      return
    }
    if (narrow) {
      setNarrow(false)
      return
    }
    setClosing(true)
    window.setTimeout(() => {
      setOpen(false)
      setNarrow(true)
      setClosing(false)
    }, 240)
  }
  const list = useRef<HTMLDivElement | null>(null)


  /** This person's order of the rail; null until read, so it does not jump. */
  const [saved, setSaved] = useState<string[] | null>(null)
  useEffect(() => {
    let cancelled = false
    withRetry(() => readSkillOrder('canvas'))
      .then((found) => {
        if (!cancelled) setSaved(found)
      })
      .catch(() => {
        if (!cancelled) setSaved([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  /*
   * The colour stays with the skill rather than with the position: it is taken
   * from where the skill sits in the platform's own order, so moving one does
   * not repaint the whole rail -- and a card dragged onto the canvas is still
   * recognisably the card that was dragged.
   */
  const shade = useMemo(() => {
    const total = Math.max(skills.length, 1)
    return new Map(skills.map((skill, index) => [skill.id, spectrumColor(index, total)]))
  }, [skills])
  const byId = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills])
  const ordered = useMemo(() => arrangeBy(skills, saved ?? []), [skills, saved])

  const reorder = useCallback((next: string[]) => {
    setSaved(next)
    void saveSkillOrder('canvas', next).catch((error: unknown) => console.warn('could not save the order', error))
  }, [])

  const order = useDragOrder({
    ids: ordered.map((skill) => skill.id),
    onReorder: reorder,
    // Inside the rail's list it rearranges; anywhere else it is on its way
    // to the canvas.
    inside: (x, y) => {
      const box = list.current?.getBoundingClientRect()
      return Boolean(box && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom)
    },
    onOutside: (id, at) => {
      const skill = byId.get(id)
      setDrag(at && skill ? { skill, glow: shade.get(id) ?? spectrumColor(0, 1), x: at.x, y: at.y } : null)
    },
    onDropOutside: (id, x, y) => {
      const skill = byId.get(id)
      if (skill) onDrop(skill, shade.get(id) ?? spectrumColor(0, 1), x, y)
    },
  })

  /*
   * Never half a card (Huanrui, 2026-10-22). On a short screen the list runs
   * out of room and scrolls inside the pill, and the pill used to cut it off
   * wherever the room ended -- half a card against the edge, a straight cut
   * across the round end. So the room is measured (the rail's box, less the
   * ball and the gap above it) and the list is made exactly a whole number
   * of cards tall; scrolling snaps card by card (canvas.css), so at rest the
   * edges always fall between cards.
   */
  const rail = useRef<HTMLElement | null>(null)
  const [rows, setRows] = useState<number | null>(null)
  const count = saved ? order.order.filter((id) => byId.has(id)).length : 0
  useLayoutEffect(() => {
    const box = rail.current
    if (!box || !open) return
    const measure = () => {
      const dock = box.querySelector<HTMLElement>('.crail__dock')
      const gap = parseFloat(getComputedStyle(box).rowGap) || 8
      // The pill's 1px edges and the full rail's 10px padding (the larger),
      // so the count is the same in both widths; a card is 40px, 6px apart.
      // And 1px spare: clientHeight is rounded, and a pill a fraction too
      // tall for the box is squeezed, clipping its foot.
      const room = box.clientHeight - (dock?.offsetHeight ?? 0) - gap - 2 - 20 - 1
      const fit = Math.max(1, Math.floor((room + RAIL_GAP) / (RAIL_CARD + RAIL_GAP)))
      setRows(count > 0 && fit < count ? fit : null)
    }
    measure()
    const watch = new ResizeObserver(measure)
    watch.observe(box)
    return () => watch.disconnect()
  }, [open, count])

  return (
    <aside
      ref={rail}
      className={`crail${open ? ' crail--open' : ' crail--shut'}${open && narrow ? ' crail--icons' : ''}${
        opening ? ' crail--opening' : ''
      }${closing ? ' crail--closing' : ''}${rows ? ' crail--limited' : ''}`}
      style={rows ? ({ '--rail-rows': rows } as React.CSSProperties) : undefined}
    >
      {/* The tools in their own pill, just above the ball (2026-10-22): they
          grow up out of the ball, widen to the left, and fold back down
          into it. Shut, the ball alone. */}
      {open ? (
        <div className="crail__pill">
        <div className="crail__body">
          <div
            ref={list}
            className="crail__list"
            onScroll={() => setTip(null)}
          >
            {saved
              ? order.order.map((id) => {
                  const skill = byId.get(id)
                  if (!skill) return null
                  return (
                    <div key={id} className="crail__slot" {...order.itemProps(id)}>
                      <RailCard
                        skill={skill}
                        glow={shade.get(id) ?? spectrumColor(0, 1)}
                        narrow={open && narrow}
                        onTip={showTip}
                      />
                    </div>
                  )
                })
              : null}
          </div>
        </div>
        </div>
      ) : null}

      <div className="crail__dock">
        <button
          type="button"
          className="crail__ball"
          onClick={toggle}
          aria-expanded={open && !closing}
          aria-label={!open ? 'Show the tools' : narrow ? 'Show the tools with names' : 'Hide the tools'}
          title={!open ? 'Tools' : narrow ? 'With names' : 'Hide the tools'}
        >
          <span className="crail__ball-mark" aria-hidden="true" />
        </button>
      </div>

      {tip && open && narrow && !closing && !drag ? (
        <div className="crail__tip" role="tooltip" style={{ right: tip.right, top: tip.top }}>
          {tip.name}
        </div>
      ) : null}

      {drag
        ? createPortal(
            <div
              className="crail__ghost"
              style={
                {
                  left: drag.x,
                  top: drag.y,
                  '--tile-glow': drag.glow,
                } as React.CSSProperties
              }
            >
              <span className="crail__glyph" aria-hidden="true">
                <SkillGlyph glyph={drag.skill.glyph} />
              </span>
              <span className="crail__name">{drag.skill.name}</span>
            </div>,
            document.body,
          )
        : null}
    </aside>
  )
}


/**
 * One skill in the rail.
 *
 * It lights up the way the overview's tiles do -- the spotlight follows the
 * cursor and the edge nearest it brightens -- because it is the same kind of
 * object and the two grids sit in the same product. What it does not have is
 * the click ripple: the press is the beginning of a drag, not a click.
 * The drag itself is on the slot around it (useDragOrder).
 */
function RailCard({
  skill,
  glow,
  narrow,
  onTip,
}: {
  skill: SkillDefinition
  glow: string
  narrow: boolean
  onTip: (name: string | null, card?: HTMLElement) => void
}) {
  const { ref, handlers } = useTileInteraction<HTMLDivElement>({ interactive: false })

  return (
    <div
      ref={ref}
      className="crail__card"
      style={{ '--tile-glow': glow } as React.CSSProperties}
      // Icons only, the name is not on the card: the bubble beside it says
      // it (and the browser's own tooltip is left off so there are not two).
      title={narrow ? undefined : skill.summary}
      onMouseEnter={(event) => {
        if (narrow) onTip(skill.name, event.currentTarget)
      }}
      onMouseLeave={() => onTip(null)}
      onMouseDown={() => onTip(null)}
      {...handlers}
    >
      <span className="crail__spot" aria-hidden="true" />
      <span className="crail__edge" aria-hidden="true" />
      <span className="crail__glyph" aria-hidden="true">
        <SkillGlyph glyph={skill.glyph} />
      </span>
      <span className="crail__name">{skill.name}</span>
    </div>
  )
}
